import { useStore } from "vuex";
import { useRouter } from "vue-router";
import axios from "axios";
import { datadogRum } from "@datadog/browser-rum";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import logAction from "@/apps/merchant3/services/action-logs.service";
import ActionLogs from "@/apps/merchant3/types/ActionLogEvents";
import {
  isEcommerceFlow,
  isPeruSaleFlow,
  isPhilippinesSaleFlow,
} from "@/apps/merchant3/helpers/checkoutFlows";
import { recorder, EVENTS } from "@/apps/merchant3/shared-services/recording.service";
import { computed, ComputedRef, getCurrentInstance, Ref, ref } from "vue";
import { acceptPrivacyPolicies } from "@/apps/merchant3/services/privacy-policy.service";
import { acceptTerms } from "@/apps/merchant3/services/termsAndConditions.service";
import { getAPIErrorMessage } from "@/apps/merchant3/shared-helpers/APIErrorMessages";
import { CartActiveFeatures } from "@/apps/merchant3/types/CartActiveFeatures";
import { RouteName as PersonRegistrationRouteName } from "@/apps/person-registration/router/RouteName";
import useIncodeWebFlow from "./useIncodeWebFlow";
import { ProductVersion } from "@/apps/merchant3/types/ProductVersion";
import { RouteName } from "@/apps/merchant3/router/RouteName";
import { getCountryCode } from "@/apps/merchant3/helpers/flowType";

type ComposableResult = {
  acceptPrivacyAndTerms: (
    cartTag: string,
    onSuccess: () => void,
    onError: (message: string, error: Error) => void
  ) => Promise<void>;
  acceptingPrivacyAndTerms: Ref<boolean>;
  requiresManualAcknowledgement: ComputedRef<boolean>;
  hasAcknowledgedTermsAndConditions: Ref<boolean>;
  hasAcknowledgedPrivacyPolicy: Ref<boolean>;
  hasAcknowledgedPrivacyAndTerms: ComputedRef<boolean>;
  handleContinueToNextPage: () => void;
};

export function useTermsAndPrivacyAcceptance(): ComposableResult {
  const { t } = useI18n();
  const store = useStore();
  const router = useRouter();
  const app = getCurrentInstance()?.appContext.app;
  const acceptingPrivacyAndTerms = ref(false);
  const hasAcknowledgedTermsAndConditions = ref(false);
  const hasAcknowledgedPrivacyPolicy = ref(false);

  const isIncodeWebFlow = useIncodeWebFlow();
  const isSelfService = computed(
    () =>
      store.state.carts?.productVersion === ProductVersion.M3_SELF_ONBOARDING
  );

  const isCheckoutOnPersonManagement = computed<boolean>(() => {
    return store.getters["carts/hasActiveFeature"](
      CartActiveFeatures.CHECKOUT_ON_PERSON_MANAGEMENT
    );
  });

  const requiresManualAcknowledgement = computed(() => {
    return isPeruSaleFlow() || isPhilippinesSaleFlow();
  });

  const hasAcknowledgedPrivacyAndTerms = computed(
    () =>
      hasAcknowledgedTermsAndConditions.value &&
      hasAcknowledgedPrivacyPolicy.value
  );

  const createMessageFromError = (error: Error): string => {
    return axios.isAxiosError(error)
      ? getAPIErrorMessage(error?.response?.data, t)
      : error.message;
  };

  const acceptPrivacyAndTerms = async (
    cartTag: string,
    onSuccess: () => void,
    onError: (message: string, error: Error) => void
  ): Promise<void> => {
    const acceptPrivacyAndTermsStart = performance.now();
    datadogRum.addAction("m3-terms-and-privacy-policy-accept-clicked");

    await logEvent(
      EventLogs.Events
        .TERMS_AND_CONDITIONS_AND_PRIVACY_POLICY_ACCEPTANCE_ATTEMPTED
    );
    await logEvent(EventLogs.Events.TC_CONTINUE);
    await logAction(ActionLogs.Events.ACCEPTED_TERMS_AND_PRIVACY);

    if (isEcommerceFlow.value) {
      recorder(app)?.recordEvent(EVENTS.ACCEPT_TERMS_AND_CONDITIONS);
    }

    acceptingPrivacyAndTerms.value = requiresManualAcknowledgement.value
      ? hasAcknowledgedPrivacyAndTerms.value
      : true;

    const acceptTermsStart = performance.now();
    try {
      const termsResponse = await acceptTerms(cartTag);

      if (termsResponse.status === 201 || termsResponse.status === 200) {
        await logEvent(
          EventLogs.Events.TERMS_AND_CONDITIONS_ACCEPTANCE_RECORDED
        );
        await logAction(ActionLogs.Events.ACCEPTED_TERMS_AND_CONDITIONS);
        await store.dispatch("carts/setCart", termsResponse.data);
      }
    } catch (error) {
      acceptingPrivacyAndTerms.value = false;
      const message = createMessageFromError(error as Error);
      onError(message, error as Error);
      return;
    } finally {
      // Track of how long it took to accept both terms in RUM.
      const acceptTermsDuration = performance.now() - acceptTermsStart;
      datadogRum.addAction("m3-api-terms-conditions-accepted", {
        loadTime: acceptTermsDuration,
      });
    }

    const acceptPrivacyStart = performance.now();
    try {
      const privacyResponse = await acceptPrivacyPolicies(cartTag);
      await store.dispatch("carts/setCart", privacyResponse.data);
      await logEvent(EventLogs.Events.PRIVACY_POLICY_ACCEPTANCE_RECORDED);
      await logAction(ActionLogs.Events.ACCEPTED_PRIVACY_POLICY);
    } catch (error) {
      acceptingPrivacyAndTerms.value = false;
      const message = createMessageFromError(error as Error);
      onError(message, error as Error);
      return;
    } finally {
      // Track of how long it took to accept privacy policy in RUM.
      const acceptPrivacyDuration = performance.now() - acceptPrivacyStart;
      datadogRum.addAction("m3-api-privacy-policy-accepted", {
        loadTime: acceptPrivacyDuration,
      });
    }

    // Track of how long it took to accept both terms in RUM.
    const acceptPrivacyAndTermsDuration =
      performance.now() - acceptPrivacyAndTermsStart;
    datadogRum.addAction("m3-api-terms-and-privacy-accepted", {
      loadTime: acceptPrivacyAndTermsDuration,
    });

    onSuccess();
  };

  const handleContinueToNextPage = () => {
    if (isCheckoutOnPersonManagement.value) {
      router.push({
        name: PersonRegistrationRouteName.PersonRegistration,
        params: {
          registrationTag: store.state.carts.lastPersonRegistrationTag,
        },
        query: {
          suggestedActionablePage:
            PersonRegistrationRouteName.IdentificationUploader,
          source: "checkout",
        },
      });
    } else {
      if (isIncodeWebFlow()) {
        if (isSelfService.value || isEcommerceFlow.value) {
          router.push({ name: RouteName.M3IncodeIntroduction, params: { countryCode: getCountryCode() } });
        } else {
          router.push({ name: RouteName.M3IncodeWebFlow, params: { countryCode: getCountryCode() } });
        }
      } else {
        router.push({ name: RouteName.M3IdentificationUploader, params: { countryCode: getCountryCode() } });
      }
    }
  };

  return {
    acceptPrivacyAndTerms,
    acceptingPrivacyAndTerms,
    requiresManualAcknowledgement,
    hasAcknowledgedTermsAndConditions,
    hasAcknowledgedPrivacyPolicy,
    hasAcknowledgedPrivacyAndTerms,
    handleContinueToNextPage,
  };
}
